/**
 * useGeometryState - State management for pure geometry objects
 *
 * Manages points, curves, surfaces, and solids as pure geometry,
 * separate from building elements (walls, floors, etc.).
 */

import { useState, useCallback, useMemo, useRef } from 'react';
import { Point, Line, Polyline, Arc, Circle, Rectangle, Polygon, GeometryType, generateId } from '../geometry';
import { Vector } from '../geometry/base/Vector';

/**
 * Initial empty geometry state
 */
const initialGeometryState = () => ({
  points: [],
  curves: [],
  surfaces: [],
  solids: [],
  layers: [
    { id: 'default', name: 'Default', color: '#ffffff', visible: true, locked: false },
    { id: 'construction', name: 'Construction', color: '#808080', visible: true, locked: false },
  ],
});

/**
 * Hook for managing geometry state
 * @param {Object} initialData - Initial geometry data
 * @returns {Object} Geometry state and operations
 */
export function useGeometryState(initialData = null, { snapThreshold: externalSnapThreshold, activeLayerId: externalActiveLayerId } = {}) {
  const [geometry, setGeometry] = useState(() => {
    if (initialData?.geometry) {
      return deserializeGeometry(initialData.geometry);
    }
    return initialGeometryState();
  });

  // Active layer ref (updated from outside, used in creation callbacks without re-creating them)
  const activeLayerRef = useRef(externalActiveLayerId || 'default');
  activeLayerRef.current = externalActiveLayerId || 'default';

  // ============= POINTS =============

  /**
   * Add a new point
   * @param {number} x
   * @param {number} y
   * @param {number} z
   * @param {Object} attributes
   * @returns {Point} Created point
   */
  const addPoint = useCallback((x, y, z = 0, attributes = {}) => {
    const point = new Point(x, y, z, { layer: activeLayerRef.current, ...attributes });
    setGeometry(prev => ({
      ...prev,
      points: [...prev.points, point],
    }));
    return point;
  }, []);

  /**
   * Move a point and update any curves that reference it
   * Rhino-style: All geometry that references this point will update automatically
   * @param {string} id - Point ID
   * @param {number} x - New X
   * @param {number} y - New Y
   * @param {number} z - New Z (optional)
   */
  const movePoint = useCallback((id, x, y, z) => {
    setGeometry(prev => {
      // Update the point
      const newPoints = prev.points.map(p => {
        if (p.id === id) {
          const newPoint = p.clone();
          newPoint.moveTo(x, y, z !== undefined ? z : p.z);
          return newPoint;
        }
        return p;
      });

      // Helper to get a point from the new points array
      const getPointFromNew = (pointId) => newPoints.find(p => p.id === pointId);

      // Update any curves that reference this point
      const newCurves = prev.curves.map(curve => {
        if (!curve.usesPointReferences) return curve;

        // Check if this curve references the moved point
        let referencesMovedPoint = false;

        // Check pointIds array (Line, Polyline, Rectangle, Arc 3-point)
        if (curve.pointIds?.includes(id)) {
          referencesMovedPoint = true;
        }

        // Check centerPointId (Circle, Arc center-based)
        if (curve.centerPointId === id) {
          referencesMovedPoint = true;
        }

        // Check radiusPointId (Circle)
        if (curve.radiusPointId === id) {
          referencesMovedPoint = true;
        }

        if (referencesMovedPoint) {
          const newCurve = curve.clone();
          newCurve.updateFromPoints(getPointFromNew);
          return newCurve;
        }

        return curve;
      });

      return {
        ...prev,
        points: newPoints,
        curves: newCurves,
      };
    });
  }, []);

  /**
   * Delete a point
   * @param {string} id
   */
  const deletePoint = useCallback((id) => {
    setGeometry(prev => ({
      ...prev,
      points: prev.points.filter(p => p.id !== id),
    }));
  }, []);

  /**
   * Get point by ID
   * @param {string} id
   * @returns {Point|undefined}
   */
  const getPoint = useCallback((id) => {
    return geometry.points.find(p => p.id === id);
  }, [geometry.points]);

  /**
   * Find nearest point to position
   * @param {number} x
   * @param {number} y
   * @param {number} maxDistance
   * @returns {Point|null}
   */
  const findNearestPoint = useCallback((x, y, maxDistance = Infinity) => {
    let nearest = null;
    let minDist = maxDistance;

    for (const point of geometry.points) {
      const dist = point.distanceToPosition(x, y, 0);
      if (dist < minDist) {
        minDist = dist;
        nearest = point;
      }
    }

    return nearest;
  }, [geometry.points]);

  /**
   * SNAP_THRESHOLD for reusing existing points (in mm)
   * If a new point is within this distance of an existing point, use the existing one
   */
  const snapThresholdRef = useRef(externalSnapThreshold || 50);
  snapThresholdRef.current = externalSnapThreshold || 50;

  // ============= CURVES =============

  /**
   * Add a line (simple, without point references)
   * @param {{x,y,z}} start
   * @param {{x,y,z}} end
   * @param {Object} attributes
   * @returns {Line}
   */
  const addLine = useCallback((start, end, attributes = {}) => {
    const line = new Line(
      Vector.fromObject(start),
      Vector.fromObject(end),
      { layer: activeLayerRef.current, ...attributes }
    );
    setGeometry(prev => ({
      ...prev,
      curves: [...prev.curves, line],
    }));
    return line;
  }, []);

  /**
   * Add a line with automatic point creation (Rhino-style)
   * Reuses existing points if close enough, otherwise creates new ones
   * @param {{x,y,z}} start
   * @param {{x,y,z}} end
   * @param {Object} attributes
   * @returns {{line: Line, points: Point[]}}
   */
  const addLineWithPoints = useCallback((start, end, attributes = {}) => {
    setGeometry(prev => {
      // Find or create start point
      let startPt = null;
      let startIsNew = false;
      for (const p of prev.points) {
        if (p.distanceToPosition(start.x, start.y, start.z || 0) < snapThresholdRef.current) {
          startPt = p;
          break;
        }
      }
      if (!startPt) {
        startPt = new Point(start.x, start.y, start.z || 0, { layer: activeLayerRef.current });
        startIsNew = true;
      }

      // Find or create end point
      let endPt = null;
      let endIsNew = false;
      const checkPoints = startIsNew ? [...prev.points, startPt] : prev.points;
      for (const p of checkPoints) {
        if (p.distanceToPosition(end.x, end.y, end.z || 0) < snapThresholdRef.current) {
          endPt = p;
          break;
        }
      }
      if (!endPt) {
        endPt = new Point(end.x, end.y, end.z || 0, { layer: activeLayerRef.current });
        endIsNew = true;
      }

      // Create line referencing the points
      const line = new Line(
        Vector.fromObject({ x: startPt.x, y: startPt.y, z: startPt.z }),
        Vector.fromObject({ x: endPt.x, y: endPt.y, z: endPt.z }),
        { layer: activeLayerRef.current, ...attributes, pointIds: [startPt.id, endPt.id] }
      );

      // Build new points array
      const newPoints = [...prev.points];
      if (startIsNew) newPoints.push(startPt);
      if (endIsNew) newPoints.push(endPt);

      return {
        ...prev,
        points: newPoints,
        curves: [...prev.curves, line],
      };
    });
  }, []);

  /**
   * Add a construction line (infinite line for alignment)
   * @param {{x,y,z}} point1 - First point
   * @param {{x,y,z}} point2 - Second point (defines direction)
   * @param {Object} attributes
   * @returns {Line}
   */
  const addConstructionLine = useCallback((point1, point2, attributes = {}) => {
    // Extend line to "infinite" length (100m in each direction)
    const dx = point2.x - point1.x;
    const dy = point2.y - point1.y;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const nx = dx / len;
    const ny = dy / len;
    const EXTEND = 100000; // 100m

    const start = { x: point1.x - nx * EXTEND, y: point1.y - ny * EXTEND, z: point1.z || 0 };
    const end = { x: point1.x + nx * EXTEND, y: point1.y + ny * EXTEND, z: point2.z || 0 };

    const line = new Line(
      Vector.fromObject(start),
      Vector.fromObject(end),
      { layer: 'construction', isConstruction: true, ...attributes }
    );
    setGeometry(prev => ({
      ...prev,
      curves: [...prev.curves, line],
    }));
    return line;
  }, []);

  /**
   * Add a polyline (simple, without point references)
   * @param {Array<{x,y,z}>} points
   * @param {boolean} closed
   * @param {Object} attributes
   * @returns {Polyline}
   */
  const addPolyline = useCallback((points, closed = false, attributes = {}) => {
    const polyline = new Polyline(
      points.map(p => Vector.fromObject(p)),
      closed,
      { layer: activeLayerRef.current, ...attributes }
    );
    setGeometry(prev => ({
      ...prev,
      curves: [...prev.curves, polyline],
    }));
    return polyline;
  }, []);

  /**
   * Add a polyline with automatic point creation (Rhino-style)
   * Reuses existing points if close enough, otherwise creates new ones
   * @param {Array<{x,y,z}>} points
   * @param {boolean} closed
   * @param {Object} attributes - can include extrudeHeight to auto-create solid
   * @returns {void}
   */
  const addPolylineWithPoints = useCallback((points, closed = false, attributes = {}) => {
    const { extrudeHeight, ...polylineAttributes } = attributes;

    setGeometry(prev => {
      const newPoints = [...prev.points];
      const polylinePointIds = [];
      const usedPoints = [];

      // For each vertex, find or create a point
      for (const pos of points) {
        let foundPt = null;
        // Check existing points and newly created ones
        for (const p of newPoints) {
          if (p.distanceToPosition(pos.x, pos.y, pos.z || 0) < snapThresholdRef.current) {
            foundPt = p;
            break;
          }
        }
        if (!foundPt) {
          foundPt = new Point(pos.x, pos.y, pos.z || 0, { layer: activeLayerRef.current });
          newPoints.push(foundPt);
        }
        polylinePointIds.push(foundPt.id);
        usedPoints.push(foundPt);
      }

      // Create polyline using actual point positions
      const polyline = new Polyline(
        usedPoints.map(p => Vector.fromObject({ x: p.x, y: p.y, z: p.z })),
        closed,
        { layer: activeLayerRef.current, ...polylineAttributes, pointIds: polylinePointIds }
      );

      // Auto-create solid if closed and extrudeHeight is specified
      let newSolids = prev.solids;
      if (closed && extrudeHeight) {
        const profilePoints = usedPoints.map(p => ({ x: p.x, y: p.y, z: p.z }));

        // Calculate centroid for origin
        let cx = 0, cy = 0;
        profilePoints.forEach(p => { cx += p.x; cy += p.y; });
        cx /= profilePoints.length;
        cy /= profilePoints.length;

        const solid = {
          id: `ext_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          type: 'Extrusion',
          origin: { x: cx, y: cy, z: extrudeHeight / 2 },
          profilePoints,
          direction: { x: 0, y: 0, z: extrudeHeight },
          color: '#6b7280',
          opacity: 0.7,
          name: null,
          sourceId: polyline.id,
        };
        newSolids = [...prev.solids, solid];
      }

      return {
        ...prev,
        points: newPoints,
        curves: [...prev.curves, polyline],
        solids: newSolids,
      };
    });
  }, []);

  /**
   * Add an arc from 3 points (simple, without point references)
   * @param {{x,y,z}} start
   * @param {{x,y,z}} through
   * @param {{x,y,z}} end
   * @param {Object} attributes
   * @returns {Arc|null}
   */
  const addArc3Point = useCallback((start, through, end, attributes = {}) => {
    const arc = Arc.from3Points(start, through, end, { layer: activeLayerRef.current, ...attributes });
    if (!arc) return null;
    setGeometry(prev => ({
      ...prev,
      curves: [...prev.curves, arc],
    }));
    return arc;
  }, []);

  /**
   * Add an arc from 3 points with automatic point creation (Rhino-style)
   * Reuses existing points if close enough, otherwise creates new ones
   * @param {{x,y,z}} start
   * @param {{x,y,z}} through
   * @param {{x,y,z}} end
   * @param {Object} attributes
   * @returns {{arc: Arc, points: Point[]}|null}
   */
  const addArc3PointWithPoints = useCallback((start, through, end, attributes = {}) => {
    setGeometry(prev => {
      const newPoints = [...prev.points];

      // Helper to find or create point
      const findOrCreatePoint = (pos) => {
        for (const p of newPoints) {
          if (p.distanceToPosition(pos.x, pos.y, pos.z || 0) < snapThresholdRef.current) {
            return { point: p, isNew: false };
          }
        }
        const pt = new Point(pos.x, pos.y, pos.z || 0, { layer: activeLayerRef.current });
        newPoints.push(pt);
        return { point: pt, isNew: true };
      };

      const { point: startPt } = findOrCreatePoint(start);
      const { point: throughPt } = findOrCreatePoint(through);
      const { point: endPt } = findOrCreatePoint(end);

      // Use actual point positions for arc
      const arc = Arc.from3Points(
        { x: startPt.x, y: startPt.y, z: startPt.z },
        { x: throughPt.x, y: throughPt.y, z: throughPt.z },
        { x: endPt.x, y: endPt.y, z: endPt.z },
        {
          layer: activeLayerRef.current,
          ...attributes,
          pointIds: [startPt.id, throughPt.id, endPt.id]
        }
      );

      if (!arc) return prev; // Points are collinear

      return {
        ...prev,
        points: newPoints,
        curves: [...prev.curves, arc],
      };
    });
  }, []);

  /**
   * Add an arc from center and angles
   * @param {{x,y,z}} center
   * @param {number} radius
   * @param {number} startAngle
   * @param {number} endAngle
   * @param {Object} attributes
   * @returns {Arc}
   */
  const addArc = useCallback((center, radius, startAngle, endAngle, attributes = {}) => {
    const arc = new Arc(
      Vector.fromObject(center),
      radius,
      startAngle,
      endAngle,
      { layer: activeLayerRef.current, ...attributes }
    );
    setGeometry(prev => ({
      ...prev,
      curves: [...prev.curves, arc],
    }));
    return arc;
  }, []);

  /**
   * Add a circle (simple, without point references)
   * @param {{x,y,z}} center
   * @param {number} radius
   * @param {Object} attributes
   * @returns {Circle}
   */
  const addCircle = useCallback((center, radius, attributes = {}) => {
    const circle = new Circle(
      Vector.fromObject(center),
      radius,
      { layer: activeLayerRef.current, ...attributes }
    );
    setGeometry(prev => ({
      ...prev,
      curves: [...prev.curves, circle],
    }));
    return circle;
  }, []);

  /**
   * Add a circle with automatic center point creation (Rhino-style)
   * Reuses existing center point if close enough, otherwise creates new one
   * @param {{x,y,z}} center
   * @param {number} radius
   * @param {Object} attributes
   * @returns {{circle: Circle, points: Point[]}}
   */
  const addCircleWithPoints = useCallback((center, radius, attributes = {}) => {
    setGeometry(prev => {
      // Find or create center point
      let centerPt = null;
      for (const p of prev.points) {
        if (p.distanceToPosition(center.x, center.y, center.z || 0) < snapThresholdRef.current) {
          centerPt = p;
          break;
        }
      }
      const isNewPoint = !centerPt;
      if (!centerPt) {
        centerPt = new Point(center.x, center.y, center.z || 0, { layer: activeLayerRef.current });
      }

      const circle = new Circle(
        Vector.fromObject({ x: centerPt.x, y: centerPt.y, z: centerPt.z }),
        radius,
        { layer: activeLayerRef.current, ...attributes, centerPointId: centerPt.id }
      );

      return {
        ...prev,
        points: isNewPoint ? [...prev.points, centerPt] : prev.points,
        curves: [...prev.curves, circle],
      };
    });
  }, []);

  /**
   * Add a circle from center and a point on the circle (Rhino-style)
   * Reuses existing points if close enough, otherwise creates new ones
   * @param {{x,y,z}} center
   * @param {{x,y,z}} pointOnCircle
   * @param {Object} attributes
   * @returns {{circle: Circle, points: Point[]}}
   */
  const addCircleFromCenterAndPoint = useCallback((center, pointOnCircle, attributes = {}) => {
    setGeometry(prev => {
      const newPoints = [...prev.points];

      // Find or create center point
      let centerPt = null;
      for (const p of newPoints) {
        if (p.distanceToPosition(center.x, center.y, center.z || 0) < snapThresholdRef.current) {
          centerPt = p;
          break;
        }
      }
      if (!centerPt) {
        centerPt = new Point(center.x, center.y, center.z || 0, { layer: activeLayerRef.current });
        newPoints.push(centerPt);
      }

      // Find or create radius point
      let radiusPt = null;
      for (const p of newPoints) {
        if (p.distanceToPosition(pointOnCircle.x, pointOnCircle.y, pointOnCircle.z || 0) < snapThresholdRef.current) {
          radiusPt = p;
          break;
        }
      }
      if (!radiusPt) {
        radiusPt = new Point(pointOnCircle.x, pointOnCircle.y, pointOnCircle.z || 0, { layer: activeLayerRef.current });
        newPoints.push(radiusPt);
      }

      // Calculate radius from actual point positions
      const c = Vector.fromObject({ x: centerPt.x, y: centerPt.y, z: centerPt.z });
      const p = Vector.fromObject({ x: radiusPt.x, y: radiusPt.y, z: radiusPt.z });
      const radius = c.distanceTo(p);

      const circle = new Circle(
        c,
        radius,
        { layer: activeLayerRef.current, ...attributes, centerPointId: centerPt.id, radiusPointId: radiusPt.id }
      );

      return {
        ...prev,
        points: newPoints,
        curves: [...prev.curves, circle],
      };
    });
  }, []);

  /**
   * Add a rectangle with automatic corner point creation
   * Reuses existing corner points if close enough, otherwise creates new ones
   * @param {{x,y,z}} corner1 - First corner
   * @param {{x,y,z}} corner2 - Opposite corner
   * @param {Object} attributes
   * @returns {{rectangle: Rectangle, points: Point[]}}
   */
  const addRectangle = useCallback((corner1, corner2, attributes = {}) => {
    setGeometry(prev => {
      const newPoints = [...prev.points];

      // Corner positions (counter-clockwise: bottomLeft, bottomRight, topRight, topLeft)
      const cornerPositions = [
        { x: corner1.x, y: corner1.y, z: corner1.z || 0 },           // 0: Bottom-left
        { x: corner2.x, y: corner1.y, z: corner1.z || 0 },           // 1: Bottom-right
        { x: corner2.x, y: corner2.y, z: corner2.z || 0 },           // 2: Top-right
        { x: corner1.x, y: corner2.y, z: corner2.z || 0 },           // 3: Top-left
      ];

      const cornerPointIds = [];
      const usedCorners = [];

      for (const pos of cornerPositions) {
        let foundPt = null;
        for (const p of newPoints) {
          if (p.distanceToPosition(pos.x, pos.y, pos.z) < snapThresholdRef.current) {
            foundPt = p;
            break;
          }
        }
        if (!foundPt) {
          foundPt = new Point(pos.x, pos.y, pos.z, { layer: activeLayerRef.current });
          newPoints.push(foundPt);
        }
        cornerPointIds.push(foundPt.id);
        usedCorners.push(foundPt);
      }

      // Create rectangle using actual corner positions
      const c1 = Vector.fromObject({ x: usedCorners[0].x, y: usedCorners[0].y, z: usedCorners[0].z });
      const c2 = Vector.fromObject({ x: usedCorners[2].x, y: usedCorners[2].y, z: usedCorners[2].z });

      const rectangle = new Rectangle(c1, c2, {
        layer: activeLayerRef.current,
        ...attributes,
        pointIds: cornerPointIds,
      });

      return {
        ...prev,
        points: newPoints,
        curves: [...prev.curves, rectangle],
      };
    });
  }, []);

  /**
   * Add a regular polygon
   * @param {{x,y,z}} center
   * @param {number} radius
   * @param {number} sides
   * @param {number} rotation
   * @param {Object} attributes
   * @returns {Polygon}
   */
  const addPolygon = useCallback((center, radius, sides = 6, rotation = 0, attributes = {}) => {
    const polygon = new Polygon(
      Vector.fromObject(center),
      radius,
      sides,
      rotation,
      { layer: activeLayerRef.current, ...attributes }
    );
    setGeometry(prev => ({
      ...prev,
      curves: [...prev.curves, polygon],
    }));
    return polygon;
  }, []);

  /**
   * Get curve by ID
   * @param {string} id
   * @returns {CurveBase|undefined}
   */
  const getCurve = useCallback((id) => {
    return geometry.curves.find(c => c.id === id);
  }, [geometry.curves]);

  /**
   * Delete a curve
   * @param {string} id
   */
  const deleteCurve = useCallback((id) => {
    setGeometry(prev => ({
      ...prev,
      curves: prev.curves.filter(c => c.id !== id),
    }));
  }, []);

  /**
   * Update a curve's control point (for dragging corners)
   * @param {string} id - Curve ID
   * @param {number} pointIndex - Index of the control point
   * @param {{x,y,z}} newPosition - New position
   */
  const updateCurveControlPoint = useCallback((id, pointIndex, newPosition) => {
    setGeometry(prev => ({
      ...prev,
      curves: prev.curves.map(curve => {
        if (curve.id !== id) return curve;

        // Rectangle: update corner
        if (curve.type === 'Rectangle') {
          const c1 = curve._corner1 || curve.corner1;
          const c2 = curve._corner2 || curve.corner2;
          if (!c1 || !c2) return curve;

          let newCorner1 = { ...c1 };
          let newCorner2 = { ...c2 };

          // Control point indices for rectangle:
          // 0: bottom-left (c1)
          // 1: bottom-right (c2.x, c1.y)
          // 2: top-right (c2)
          // 3: top-left (c1.x, c2.y)
          switch (pointIndex) {
            case 0: // bottom-left
              newCorner1 = { x: newPosition.x, y: newPosition.y, z: newPosition.z || 0 };
              break;
            case 1: // bottom-right
              newCorner2 = { ...newCorner2, x: newPosition.x };
              newCorner1 = { ...newCorner1, y: newPosition.y };
              break;
            case 2: // top-right
              newCorner2 = { x: newPosition.x, y: newPosition.y, z: newPosition.z || 0 };
              break;
            case 3: // top-left
              newCorner1 = { ...newCorner1, x: newPosition.x };
              newCorner2 = { ...newCorner2, y: newPosition.y };
              break;
          }

          // Create new Rectangle
          return new Rectangle(
            Vector.fromObject(newCorner1),
            Vector.fromObject(newCorner2),
            curve.attributes
          );
        }

        // Polyline: update vertex
        if (curve.type === 'Polyline' && curve._points) {
          const newPoints = [...curve._points];
          if (pointIndex >= 0 && pointIndex < newPoints.length) {
            newPoints[pointIndex] = Vector.fromObject(newPosition);
          }
          return new Polyline(newPoints, curve._closed, curve.attributes);
        }

        // Polygon: update vertex (recreates with new point)
        if (curve.type === 'Polygon' && curve._points) {
          const newPoints = [...curve._points];
          if (pointIndex >= 0 && pointIndex < newPoints.length) {
            newPoints[pointIndex] = Vector.fromObject(newPosition);
          }
          // Note: For regular polygon, this makes it irregular
          // We keep it as a Polyline after modification
          return new Polyline(newPoints, true, curve.attributes);
        }

        // Line: update endpoint
        if (curve.type === 'Line') {
          const start = curve._start || curve.start;
          const end = curve._end || curve.end;
          if (pointIndex === 0) {
            return new Line(Vector.fromObject(newPosition), end, curve.attributes);
          } else if (pointIndex === 1) {
            return new Line(start, Vector.fromObject(newPosition), curve.attributes);
          }
        }

        // Circle: move center
        if (curve.type === 'Circle' && pointIndex === 0) {
          return new Circle(Vector.fromObject(newPosition), curve._radius, curve.attributes);
        }

        return curve;
      }),
    }));
  }, []);

  /**
   * Move a curve to a new center position
   * @param {string} id - Curve ID
   * @param {{x,y,z}} newCenter - New center position
   */
  const moveCurve = useCallback((id, newCenter) => {
    setGeometry(prev => ({
      ...prev,
      curves: prev.curves.map(curve => {
        if (curve.id !== id) return curve;

        // Calculate current center
        let currentCenter = { x: 0, y: 0, z: 0 };

        if (curve.type === 'Rectangle') {
          const c1 = curve._corner1 || curve.corner1;
          const c2 = curve._corner2 || curve.corner2;
          if (c1 && c2) {
            currentCenter = {
              x: (c1.x + c2.x) / 2,
              y: (c1.y + c2.y) / 2,
              z: ((c1.z || 0) + (c2.z || 0)) / 2,
            };
          }
        } else if (curve.type === 'Circle' && curve._center) {
          currentCenter = { x: curve._center.x, y: curve._center.y, z: curve._center.z || 0 };
        } else if ((curve.type === 'Polygon' || curve.type === 'Polyline') && curve._points) {
          curve._points.forEach(p => {
            currentCenter.x += p.x;
            currentCenter.y += p.y;
            currentCenter.z += (p.z || 0);
          });
          currentCenter.x /= curve._points.length;
          currentCenter.y /= curve._points.length;
          currentCenter.z /= curve._points.length;
        } else if (curve.type === 'Line') {
          const start = curve._start || curve.start;
          const end = curve._end || curve.end;
          if (start && end) {
            currentCenter = {
              x: (start.x + end.x) / 2,
              y: (start.y + end.y) / 2,
              z: ((start.z || 0) + (end.z || 0)) / 2,
            };
          }
        }

        // Calculate delta
        const dx = newCenter.x - currentCenter.x;
        const dy = newCenter.y - currentCenter.y;
        const dz = (newCenter.z || 0) - currentCenter.z;

        // Apply translation based on curve type
        if (curve.type === 'Rectangle') {
          const c1 = curve._corner1 || curve.corner1;
          const c2 = curve._corner2 || curve.corner2;
          return new Rectangle(
            Vector.fromObject({ x: c1.x + dx, y: c1.y + dy, z: (c1.z || 0) + dz }),
            Vector.fromObject({ x: c2.x + dx, y: c2.y + dy, z: (c2.z || 0) + dz }),
            curve.attributes
          );
        } else if (curve.type === 'Circle') {
          const c = curve._center;
          return new Circle(
            Vector.fromObject({ x: c.x + dx, y: c.y + dy, z: (c.z || 0) + dz }),
            curve._radius,
            curve.attributes
          );
        } else if (curve.type === 'Polygon') {
          // Move center while keeping shape
          const newPoints = curve._points.map(p =>
            Vector.fromObject({ x: p.x + dx, y: p.y + dy, z: (p.z || 0) + dz })
          );
          return new Polyline(newPoints, true, curve.attributes);
        } else if (curve.type === 'Polyline') {
          const newPoints = curve._points.map(p =>
            Vector.fromObject({ x: p.x + dx, y: p.y + dy, z: (p.z || 0) + dz })
          );
          return new Polyline(newPoints, curve._closed, curve.attributes);
        } else if (curve.type === 'Line') {
          const start = curve._start || curve.start;
          const end = curve._end || curve.end;
          return new Line(
            Vector.fromObject({ x: start.x + dx, y: start.y + dy, z: (start.z || 0) + dz }),
            Vector.fromObject({ x: end.x + dx, y: end.y + dy, z: (end.z || 0) + dz }),
            curve.attributes
          );
        }

        return curve;
      }),
    }));
  }, []);

  /**
   * Find nearest curve to position
   * @param {number} x
   * @param {number} y
   * @param {number} maxDistance
   * @returns {{curve: CurveBase, distance: number, parameter: number}|null}
   */
  const findNearestCurve = useCallback((x, y, maxDistance = Infinity) => {
    let nearest = null;
    let minDist = maxDistance;
    let nearestParam = 0;

    const testPoint = new Vector(x, y, 0);

    for (const curve of geometry.curves) {
      const { point, parameter, distance } = curve.closestPoint(testPoint);
      if (distance < minDist) {
        minDist = distance;
        nearest = curve;
        nearestParam = parameter;
      }
    }

    return nearest ? { curve: nearest, distance: minDist, parameter: nearestParam } : null;
  }, [geometry.curves]);

  // ============= SOLIDS =============

  /**
   * Add a box solid
   * @param {{x,y,z}} origin - Center point
   * @param {number} width
   * @param {number} depth
   * @param {number} height
   * @param {Object} attributes
   * @returns {Object} Created box solid data
   */
  const addBox = useCallback((origin, width, depth, height, attributes = {}) => {
    const solid = {
      id: `box_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'Box',
      origin: { ...origin },
      rotation: { x: 0, y: 0, z: 0 },
      dimensions: { width, depth, height },
      color: attributes.color || '#6b7280',
      opacity: attributes.opacity ?? 0.7,
      name: attributes.name || null,
      ...attributes,
    };
    setGeometry(prev => ({
      ...prev,
      solids: [...prev.solids, solid],
    }));
    return solid;
  }, []);

  /**
   * Add a cylinder solid
   * @param {{x,y,z}} baseCenter - Center of base circle
   * @param {number} radius
   * @param {number} height
   * @param {Object} attributes
   * @returns {Object} Created cylinder solid data
   */
  const addCylinder = useCallback((baseCenter, radius, height, attributes = {}) => {
    const solid = {
      id: `cyl_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'Cylinder',
      origin: { x: baseCenter.x, y: baseCenter.y, z: (baseCenter.z || 0) + height / 2 },
      rotation: { x: 0, y: 0, z: 0 },
      dimensions: { radius, height, segments: 32 },
      color: attributes.color || '#6b7280',
      opacity: attributes.opacity ?? 0.7,
      name: attributes.name || null,
      ...attributes,
    };
    setGeometry(prev => ({
      ...prev,
      solids: [...prev.solids, solid],
    }));
    return solid;
  }, []);

  /**
   * Add a sphere solid
   * @param {{x,y,z}} center
   * @param {number} radius
   * @param {Object} attributes
   * @returns {Object} Created sphere solid data
   */
  const addSphere = useCallback((center, radius, attributes = {}) => {
    const solid = {
      id: `sph_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'Sphere',
      origin: { ...center },
      rotation: { x: 0, y: 0, z: 0 },
      dimensions: { radius, widthSegments: 32, heightSegments: 16 },
      color: attributes.color || '#6b7280',
      opacity: attributes.opacity ?? 0.7,
      name: attributes.name || null,
      ...attributes,
    };
    setGeometry(prev => ({
      ...prev,
      solids: [...prev.solids, solid],
    }));
    return solid;
  }, []);

  /**
   * Add an extrusion solid from a curve
   * @param {CurveBase|string} curveOrId - Curve or curve ID
   * @param {number} height - Extrusion height (Z direction)
   * @param {Object} attributes
   * @returns {Object} Created extrusion solid data
   */
  const addExtrusion = useCallback((curveOrId, height, attributes = {}) => {
    // Get the curve if ID was passed
    const curve = typeof curveOrId === 'string'
      ? geometry.curves.find(c => c.id === curveOrId)
      : curveOrId;

    if (!curve) {
      console.warn('Curve not found for extrusion');
      return null;
    }

    // Get profile points from curve
    const profilePoints = curve.points
      ? curve.points.map(p => ({ x: p.x, y: p.y, z: p.z || 0 }))
      : (curve.getPoints ? curve.getPoints(64).map(p => ({ x: p.x, y: p.y, z: p.z || 0 })) : []);

    if (profilePoints.length < 3) {
      console.warn('Need at least 3 points for extrusion');
      return null;
    }

    // Calculate centroid for origin
    let cx = 0, cy = 0;
    profilePoints.forEach(p => { cx += p.x; cy += p.y; });
    cx /= profilePoints.length;
    cy /= profilePoints.length;

    const solid = {
      id: `ext_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: 'Extrusion',
      origin: { x: cx, y: cy, z: height / 2 },
      profilePoints,
      direction: { x: 0, y: 0, z: height },
      color: attributes.color || '#6b7280',
      opacity: attributes.opacity ?? 0.7,
      name: attributes.name || null,
      sourceId: curve.id,
      ...attributes,
    };
    setGeometry(prev => ({
      ...prev,
      solids: [...prev.solids, solid],
    }));
    return solid;
  }, [geometry.curves]);

  /**
   * Get solid by ID
   * @param {string} id
   * @returns {Object|undefined}
   */
  const getSolid = useCallback((id) => {
    return geometry.solids.find(s => s.id === id);
  }, [geometry.solids]);

  /**
   * Delete a solid
   * @param {string} id
   */
  const deleteSolid = useCallback((id) => {
    setGeometry(prev => ({
      ...prev,
      solids: prev.solids.filter(s => s.id !== id),
    }));
  }, []);

  /**
   * Update a solid
   * @param {string} id
   * @param {Object} updates
   */
  const updateSolid = useCallback((id, updates) => {
    setGeometry(prev => ({
      ...prev,
      solids: prev.solids.map(s => s.id === id ? { ...s, ...updates } : s),
    }));
  }, []);

  /**
   * Update a solid's control point (for dragging corners)
   * @param {string} id - Solid ID
   * @param {number} pointIndex - Index of the control point
   * @param {{x,y,z}} newPosition - New position
   */
  const updateSolidControlPoint = useCallback((id, pointIndex, newPosition) => {
    setGeometry(prev => ({
      ...prev,
      solids: prev.solids.map(solid => {
        if (solid.id !== id) return solid;

        // Box: update corner (adjusts origin and dimensions)
        if (solid.type === 'Box' || solid.type === 'box') {
          const { origin, dimensions } = solid;
          const { width, depth, height } = dimensions;
          const hw = width / 2;
          const hd = depth / 2;
          const hh = height / 2;

          // Current corners (bottom 4 for 2D, all 8 for 3D)
          // Index: 0-3 bottom, 4-7 top
          // Calculate opposite corner index
          const oppositeIndex = (pointIndex < 4)
            ? (pointIndex + 2) % 4        // Bottom corners
            : 4 + ((pointIndex - 4 + 2) % 4); // Top corners

          // Get current corners
          const corners = [
            // Bottom
            { x: origin.x - hw, y: origin.y - hd, z: origin.z - hh },
            { x: origin.x + hw, y: origin.y - hd, z: origin.z - hh },
            { x: origin.x + hw, y: origin.y + hd, z: origin.z - hh },
            { x: origin.x - hw, y: origin.y + hd, z: origin.z - hh },
            // Top
            { x: origin.x - hw, y: origin.y - hd, z: origin.z + hh },
            { x: origin.x + hw, y: origin.y - hd, z: origin.z + hh },
            { x: origin.x + hw, y: origin.y + hd, z: origin.z + hh },
            { x: origin.x - hw, y: origin.y + hd, z: origin.z + hh },
          ];

          // Update the dragged corner
          corners[pointIndex] = { ...newPosition };

          // When dragging a corner, the opposite diagonal corner stays fixed
          // Calculate new bounding box from the dragged and opposite corners
          let minX, maxX, minY, maxY, minZ, maxZ;

          if (pointIndex < 4) {
            // Dragging bottom corner - keep same Z (height)
            const oppCorner = corners[(pointIndex + 2) % 4];
            minX = Math.min(newPosition.x, oppCorner.x);
            maxX = Math.max(newPosition.x, oppCorner.x);
            minY = Math.min(newPosition.y, oppCorner.y);
            maxY = Math.max(newPosition.y, oppCorner.y);
            minZ = origin.z - hh;
            maxZ = origin.z + hh;
          } else {
            // Dragging top corner - can change height
            const bottomIndex = pointIndex - 4;
            const oppBottomIndex = (bottomIndex + 2) % 4;
            const oppCorner = corners[oppBottomIndex];
            const oppTopCorner = corners[4 + oppBottomIndex];

            minX = Math.min(newPosition.x, oppTopCorner.x);
            maxX = Math.max(newPosition.x, oppTopCorner.x);
            minY = Math.min(newPosition.y, oppTopCorner.y);
            maxY = Math.max(newPosition.y, oppTopCorner.y);
            minZ = Math.min(corners[bottomIndex].z, newPosition.z);
            maxZ = Math.max(corners[bottomIndex].z, newPosition.z);
          }

          const newWidth = Math.max(100, maxX - minX); // Min 10cm
          const newDepth = Math.max(100, maxY - minY);
          const newHeight = Math.max(100, maxZ - minZ);

          return {
            ...solid,
            origin: {
              x: (minX + maxX) / 2,
              y: (minY + maxY) / 2,
              z: (minZ + maxZ) / 2,
            },
            dimensions: {
              width: newWidth,
              depth: newDepth,
              height: newHeight,
            },
          };
        }

        // Cylinder: move center
        if ((solid.type === 'Cylinder' || solid.type === 'cylinder') && pointIndex === 0) {
          return {
            ...solid,
            origin: { ...newPosition },
          };
        }

        // Sphere: move center
        if ((solid.type === 'Sphere' || solid.type === 'sphere') && pointIndex === 0) {
          return {
            ...solid,
            origin: { ...newPosition },
          };
        }

        return solid;
      }),
    }));
  }, []);

  /**
   * Move a solid to a new center position
   * @param {string} id - Solid ID
   * @param {{x,y,z}} newCenter - New center position
   */
  const moveSolid = useCallback((id, newCenter) => {
    setGeometry(prev => ({
      ...prev,
      solids: prev.solids.map(solid => {
        if (solid.id !== id) return solid;

        // Simply update origin for all solid types
        return {
          ...solid,
          origin: {
            x: newCenter.x,
            y: newCenter.y,
            z: solid.origin.z, // Keep Z for now (2D mode)
          },
        };
      }),
    }));
  }, []);

  // ============= GENERIC OPERATIONS =============

  /**
   * Delete geometry by ID
   * @param {string} id
   */
  const deleteGeometry = useCallback((id) => {
    setGeometry(prev => ({
      ...prev,
      points: prev.points.filter(p => p.id !== id),
      curves: prev.curves.filter(c => c.id !== id),
      surfaces: prev.surfaces.filter(s => s.id !== id),
      solids: prev.solids.filter(s => s.id !== id),
    }));
  }, []);

  /**
   * Delete multiple geometries
   * @param {string[]} ids
   */
  const deleteGeometries = useCallback((ids) => {
    const idSet = new Set(ids);
    setGeometry(prev => ({
      ...prev,
      points: prev.points.filter(p => !idSet.has(p.id)),
      curves: prev.curves.filter(c => !idSet.has(c.id)),
      surfaces: prev.surfaces.filter(s => !idSet.has(s.id)),
      solids: prev.solids.filter(s => !idSet.has(s.id)),
    }));
  }, []);

  /**
   * Get any geometry by ID
   * @param {string} id
   * @returns {GeometryBase|undefined}
   */
  const getGeometry = useCallback((id) => {
    return (
      geometry.points.find(p => p.id === id) ||
      geometry.curves.find(c => c.id === id) ||
      geometry.surfaces.find(s => s.id === id) ||
      geometry.solids.find(s => s.id === id)
    );
  }, [geometry]);

  /**
   * Transform geometry
   * @param {string|string[]} ids - ID or array of IDs
   * @param {Object} transform - {translate, rotate, scale, mirror}
   * @param {boolean} copy - Create copies instead of modifying
   * @returns {GeometryBase[]} Transformed (or new) geometries
   */
  const transformGeometry = useCallback((ids, transform, copy = false) => {
    const idArray = Array.isArray(ids) ? ids : [ids];
    const results = [];

    setGeometry(prev => {
      const newState = { ...prev };

      const processArray = (arr, key) => {
        const newArr = [];
        for (const item of arr) {
          if (idArray.includes(item.id)) {
            const transformed = item.transform(transform);
            if (copy) {
              transformed.id = generateId(item.id.split('_')[0]);
              newArr.push(item);
              newArr.push(transformed);
              results.push(transformed);
            } else {
              newArr.push(transformed);
              results.push(transformed);
            }
          } else {
            newArr.push(item);
          }
        }
        return newArr;
      };

      newState.points = processArray(prev.points, 'points');
      newState.curves = processArray(prev.curves, 'curves');
      newState.surfaces = processArray(prev.surfaces, 'surfaces');
      newState.solids = processArray(prev.solids, 'solids');

      return newState;
    });

    return results;
  }, []);

  // ============= LAYERS =============

  /**
   * Add a layer
   * @param {string} name
   * @param {string} color
   * @returns {Object} Created layer
   */
  const addLayer = useCallback((nameOrLayer, color = '#ffffff') => {
    const layer = (typeof nameOrLayer === 'object' && nameOrLayer !== null)
      ? nameOrLayer
      : {
          id: generateId('layer'),
          name: nameOrLayer,
          color,
          visible: true,
          locked: false,
        };
    setGeometry(prev => ({
      ...prev,
      layers: [...prev.layers, layer],
    }));
    return layer;
  }, []);

  /**
   * Update layer
   * @param {string} id
   * @param {Object} updates
   */
  const updateLayer = useCallback((id, updates) => {
    setGeometry(prev => ({
      ...prev,
      layers: prev.layers.map(l => l.id === id ? { ...l, ...updates } : l),
    }));
  }, []);

  /**
   * Delete layer (moves geometry to default)
   * @param {string} id
   */
  const deleteLayer = useCallback((id) => {
    if (id === 'default') return; // Can't delete default layer

    setGeometry(prev => {
      const moveToDefault = (items) => items.map(item => {
        if (item.attributes.layer === id) {
          const newItem = item.clone();
          newItem.setAttribute('layer', 'default');
          return newItem;
        }
        return item;
      });

      return {
        ...prev,
        layers: prev.layers.filter(l => l.id !== id),
        points: moveToDefault(prev.points),
        curves: moveToDefault(prev.curves),
        surfaces: moveToDefault(prev.surfaces),
        solids: moveToDefault(prev.solids),
      };
    });
  }, []);

  /**
   * Set layer for multiple geometry objects
   * @param {string[]} ids - Geometry IDs
   * @param {string} layerId - Target layer ID
   */
  const setGeometryLayer = useCallback((ids, layerId) => {
    const idSet = new Set(ids);
    const moveItem = (item) => {
      if (idSet.has(item.id)) {
        const newItem = item.clone();
        newItem.setAttribute('layer', layerId);
        return newItem;
      }
      return item;
    };
    setGeometry(prev => ({
      ...prev,
      points: prev.points.map(moveItem),
      curves: prev.curves.map(moveItem),
      surfaces: prev.surfaces.map(moveItem),
      solids: prev.solids.map(moveItem),
    }));
  }, []);

  // ============= SERIALIZATION =============

  /**
   * Serialize geometry state to JSON
   * @returns {Object}
   */
  const toJSON = useCallback(() => {
    return {
      points: geometry.points.map(p => p.toJSON()),
      curves: geometry.curves.map(c => c.toJSON()),
      surfaces: geometry.surfaces.map(s => s.toJSON()),
      solids: geometry.solids.map(s => s.toJSON()),
      layers: [...geometry.layers],
    };
  }, [geometry]);

  /**
   * Load geometry from JSON
   * @param {Object} json
   */
  const fromJSON = useCallback((json) => {
    setGeometry(deserializeGeometry(json));
  }, []);

  /**
   * Clear all geometry
   */
  const clear = useCallback(() => {
    setGeometry(initialGeometryState());
  }, []);

  // ============= COMPUTED VALUES =============

  /**
   * All geometry items (for iteration)
   */
  const allGeometry = useMemo(() => [
    ...geometry.points,
    ...geometry.curves,
    ...geometry.surfaces,
    ...geometry.solids,
  ], [geometry]);

  /**
   * Geometry counts
   */
  const counts = useMemo(() => ({
    points: geometry.points.length,
    curves: geometry.curves.length,
    surfaces: geometry.surfaces.length,
    solids: geometry.solids.length,
    total: allGeometry.length,
  }), [geometry, allGeometry]);

  /**
   * Counts per layer (for layer panel)
   */
  const layerCounts = useMemo(() => {
    const counts = {};
    geometry.layers.forEach(l => { counts[l.id] = 0; });

    // Count all geometry items per layer
    [...geometry.points, ...geometry.curves, ...geometry.surfaces, ...geometry.solids].forEach(item => {
      const layerId = item.attributes?.layer || 'default';
      counts[layerId] = (counts[layerId] || 0) + 1;
    });

    return counts;
  }, [geometry]);

  /**
   * Visible geometry (filtered by layer visibility)
   */
  const visibleGeometry = useMemo(() => {
    const visibleLayers = new Set(
      geometry.layers.filter(l => l.visible).map(l => l.id)
    );

    return {
      points: geometry.points.filter(p => visibleLayers.has(p.attributes?.layer || 'default')),
      curves: geometry.curves.filter(c => visibleLayers.has(c.attributes?.layer || 'default')),
      surfaces: geometry.surfaces.filter(s => visibleLayers.has(s.attributes?.layer || 'default')),
      solids: geometry.solids.filter(s => visibleLayers.has(s.attributes?.layer || 'default')),
    };
  }, [geometry]);

  return {
    // State
    geometry,
    points: geometry.points,
    curves: geometry.curves,
    surfaces: geometry.surfaces,
    solids: geometry.solids,
    layers: geometry.layers,

    // Computed
    allGeometry,
    visibleGeometry,
    counts,
    layerCounts,

    // Point operations
    addPoint,
    movePoint,
    deletePoint,
    getPoint,
    findNearestPoint,

    // Curve operations (simple - without point references)
    addLine,
    addConstructionLine,
    addPolyline,
    addArc3Point,
    addArc,
    addCircle,
    addRectangle,
    addPolygon,
    getCurve,
    deleteCurve,
    findNearestCurve,

    // Curve operations (Rhino-style - with automatic point creation)
    addLineWithPoints,
    addPolylineWithPoints,
    addArc3PointWithPoints,
    addCircleWithPoints,
    addCircleFromCenterAndPoint,

    // Solid operations
    addBox,
    addCylinder,
    addSphere,
    addExtrusion,
    getSolid,
    deleteSolid,
    updateSolid,
    updateSolidControlPoint,

    // Curve operations (movement)
    updateCurveControlPoint,
    moveCurve,

    // Solid operations (movement)
    moveSolid,

    // Generic operations
    getGeometry,
    deleteGeometry,
    deleteGeometries,
    transformGeometry,

    // Layer operations
    addLayer,
    updateLayer,
    deleteLayer,
    setGeometryLayer,

    // Serialization
    toJSON,
    fromJSON,
    clear,
  };
}

/**
 * Deserialize geometry from JSON
 * @param {Object} json
 * @returns {Object} Geometry state
 */
function deserializeGeometry(json) {
  return {
    points: (json.points || []).map(p => Point.fromJSON(p)),
    curves: (json.curves || []).map(c => {
      switch (c.type) {
        case GeometryType.LINE:
        case 'Line':
          return Line.fromJSON(c);
        case GeometryType.POLYLINE:
        case 'Polyline':
          return Polyline.fromJSON(c);
        case GeometryType.ARC:
        case 'Arc':
          return Arc.fromJSON(c);
        case GeometryType.CIRCLE:
        case 'Circle':
          return Circle.fromJSON(c);
        case GeometryType.RECTANGLE:
        case 'Rectangle':
          return Rectangle.fromJSON(c);
        case GeometryType.POLYGON:
        case 'Polygon':
          return Polygon.fromJSON(c);
        default:
          console.warn(`Unknown curve type: ${c.type}`);
          return null;
      }
    }).filter(Boolean),
    surfaces: json.surfaces || [],
    solids: json.solids || [],
    layers: json.layers || [
      { id: 'default', name: 'Default', color: '#ffffff', visible: true, locked: false },
    ],
  };
}

export default useGeometryState;
